import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  Refmodel
} from '@q9elements/ui-kit/common';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ManageCurrentSfProcessService} from '../../../../../services/manage-current-sf-process.service';

import {DataPopulationV3ProgressInterface} from '../../../../models/DataPopulationV3Progress.interface';
import {FirebaseSubscriptionsService} from '../../../../services/firebase-subscriptions.service';

@Component({
  selector: 'app-additional-sync-statuses',
  templateUrl: './additional-sync-statuses.component.html',
  styleUrls: ['./additional-sync-statuses.component.scss']
})
export class AdditionalSyncStatusesComponent implements OnInit, OnDestroy {
  @Input() nodePathLength: boolean;

  public currentProcess$: Observable<string | null>;
  public fieldImpact: {progress: string};
  public fetchMpProgress: any;
  public dataPopulationV3Progress?: DataPopulationV3ProgressInterface;
  public dataPopulationV3Processing: boolean;
  public dataIntegrationProcessing: boolean;
  private destroySubject: Subject<void> = new Subject();
  private refModel: Refmodel;
  private node: RefModelNode;

  constructor(
    private rmeStore: RefModelStoreService,
    private firebaseSubscriptionsService: FirebaseSubscriptionsService,
    private changeDetectorRef: ChangeDetectorRef,
    private manageCurrentProcessService: ManageCurrentSfProcessService
  ) {}

  ngOnInit() {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((refModel: Refmodel) => {
        if (refModel) {
          this.refModel = Object.assign({}, refModel);
          this.firebaseSubscriptionsService.subscribeToFieldImpactFetchProgress(
            refModel._id,
            this.onFieldImpactFProgressUpdate.bind(this)
          );
          this.firebaseSubscriptionsService.subscribeToRefModelFieldDataIntegration(
            refModel._id,
            this.onFieldDataIntegrationProgressUpdate.bind(this)
          );
          this.firebaseSubscriptionsService.subscribeToDataPopulationV3Progress(
            refModel._id,
            refModel.lastSyncUUID,
            this.onDataPopulationV3ProgressUpdate.bind(this)
          );
        }
      });

    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((node: RefModelNode) => {
        if (node) {
          this.node = Object.assign({}, node);
          this.firebaseSubscriptionsService.subscribeToMpDpFetchProgress(
            this.refModel._id,
            this.node._id,
            this.onMpDpFetching.bind(this)
          );
        }
      });

    this.currentProcess$ = this.rmeStore.select(REF_MODEL_STORE.CURRENT_SF_PROCESS);
  }

  /**
   * Callbacks for firebase subscriptions
   * **/
  private onFieldImpactFProgressUpdate(snapshot) {
    if (snapshot.val() && Object.keys(snapshot.val()).length) {
      for (const uuid of Object.keys(snapshot.val())) {
        const status = snapshot.val()[uuid].status;
        if (uuid && status === 'running') {
          this.manageCurrentProcessService.sfProcessStart('field-impact');
          this.fieldImpact = snapshot.val()[uuid];
        }
      }
    } else {
      this.fieldImpact = null;
      this.manageCurrentProcessService.sfProcessStop('field-impact');
    }
    this.changeDetectorRef.detectChanges();
  }

  private onMpDpFetching(progress) {
    if (!progress) {
      this.fetchMpProgress = null;
      this.manageCurrentProcessService.sfProcessStop('dp-mp');
    }
    if (progress && (progress.status === 'running' || progress.status === 'started')) {
      this.fetchMpProgress = Object.assign({}, progress);
      this.manageCurrentProcessService.sfProcessStart('dp-mp');
    }
    this.changeDetectorRef.detectChanges();
  }

  private onFieldDataIntegrationProgressUpdate(snapshot) {
    if (snapshot && snapshot.val() && snapshot.val().running) {
      this.dataIntegrationProcessing = true;
      this.manageCurrentProcessService.sfProcessStart('data-integration');
    } else {
      this.dataIntegrationProcessing = false;
      this.manageCurrentProcessService.sfProcessStop('data-integration');
    }
    this.changeDetectorRef.detectChanges();
  }

  private onDataPopulationV3ProgressUpdate(snapshot) {
    const progress = snapshot.val();
    this.dataPopulationV3Processing =
      progress?.status === 'active' && progress?.currentStep?.name !== 'finish_data_population';
    const dataPopulationV3ProcessAction = this.dataPopulationV3Processing
      ? 'sfProcessStart'
      : 'sfProcessStop';

    this.manageCurrentProcessService[dataPopulationV3ProcessAction]('dataPopulationV3');
    this.dataPopulationV3Progress = this.dataPopulationV3Processing ? progress : null;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Methods for unsubscription from firebase service
   * */
  private onFieldImpactEnd() {
    this.fieldImpact = null;
    this.firebaseSubscriptionsService.unsubscribeFromFieldImpactFetchProgress(this.refModel._id);
  }

  private onMpDpFetchingEnd() {
    this.firebaseSubscriptionsService.unsubscribeToMpDpFetchProgress(
      this.refModel._id,
      this.node._id
    );
  }

  private onDataIntegrationEnd() {
    this.firebaseSubscriptionsService.unsubscribeToRefModelFieldDataIntegration(this.refModel._id);
  }

  private onDataPopulationV3End() {
    this.dataPopulationV3Processing = false;
    this.firebaseSubscriptionsService.unsubscribeFromDataPopulationV3Progress(
      this.refModel._id,
      this.refModel.lastSyncUUID
    );
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.onFieldImpactEnd();
    this.onMpDpFetchingEnd();
    this.onDataIntegrationEnd();
    this.onDataPopulationV3End();
    this.rmeStore.set(REF_MODEL_STORE.CURRENT_SF_PROCESS, undefined);
  }
}
